import React, { useEffect, useState, useRef } from 'react';
import * as THREE from 'three';
import { holeDataCache } from './holeDataCache';
import { useLoadingTracker } from '../../contexts/LoadingTrackerContext';
import SelectedHoleHighlight from '../../clickTools/SelectedHoleHighlight.js';
import { useHoleDisplayRadii } from '../../store/holeDisplayStore';

const assayColumns = {
  'HoleID': 'Hole_ID',
  'from': 'Depth_From',
  'to': 'Depth_To',
  'grade': 'Au_ppm',
};
const HOLE_UPLOAD_YIELD_BATCH = 2000;
const ASSAY_UPLOAD_YIELD_BATCH = 4000;
const yieldToMainThread = () => new Promise((resolve) => setTimeout(resolve, 0));

const createHoleMatrixFromPoints = (startPoint, endPoint) => {
  const startVector = new THREE.Vector3(startPoint.x, startPoint.y, startPoint.z);
  const endVector = new THREE.Vector3(endPoint.x, endPoint.y, endPoint.z);
  const direction = new THREE.Vector3().subVectors(endVector, startVector);
  const length = Math.max(direction.length(), 0.05);
  const center = new THREE.Vector3().addVectors(startVector, endVector).multiplyScalar(0.5);
  const quaternion = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    direction.normalize()
  );

  return new THREE.Matrix4().compose(
    center,
    quaternion,
    new THREE.Vector3(1, length, 1)
  );
};

export default function CachedHoleRender({ key, name, hole_url, assay_url, dataset, holetype, visible, onLoadComplete, onLoadFailed }) {
  const [combinedData, setCombinedData] = useState({});
  const [filterData, setFilterData] = useState({});
  const [filteredHoleIds] = useState([]);
  const [holeCount, setHoleCount] = useState(0);
  const [assayCount, setAssayCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { startLoading, stopLoading, markAsFailed } = useLoadingTracker();
  const objectName = name || `CachedHoles_${dataset}_${holetype}`;

  useEffect(() => {
    let cancelled = false;
    
    // Start tracking loading
    startLoading(objectName);

    const loadData = async (attempt = 0) => {
      if (cancelled) return;

      setLoading(true);
      try {
        // Get data from cache (loads once, then uses cache)
        // The cache will retry indefinitely for network errors
        await holeDataCache.getData(hole_url, assay_url);
        if (cancelled) return;
        
        // Filter data for specific dataset and hole type
        const filteredData = holeDataCache.getFilteredData(hole_url, assay_url, dataset, holetype);
        
        if (!cancelled) {
          setCombinedData(filteredData);
          setLoading(false);
          stopLoading(objectName);
          onLoadComplete?.(objectName);
          if (attempt > 0) {
            console.log(`[CachedHoleRender] Successfully loaded ${hole_url}/${assay_url} after ${attempt + 1} attempt(s)`);
          }
        }
      } catch (error) {
        if (cancelled) return;

        // Check if it's a retryable error
        const errorMessage = error?.message || error?.toString() || String(error) || '';
        const errorString = errorMessage.toLowerCase();
        const errorName = (error?.name || '').toLowerCase();
        const errorType = (error?.type || '').toLowerCase();

        const isHttp2Error = 
          errorString.includes('http2') ||
          errorString.includes('err_http2_protocol_error') ||
          errorString.includes('protocol_error') ||
          errorName.includes('http2') ||
          errorType.includes('http2');

        const isNetworkError = 
          errorString.includes('failed to fetch') ||
          errorString.includes('networkerror') ||
          errorString.includes('network') ||
          errorString.includes('aborted') ||
          errorString.includes('timeout') ||
          errorString.includes('load failed') ||
          errorString.includes('could not load') ||
          errorString.includes('fetch') ||
          errorName.includes('network') ||
          errorName.includes('abort') ||
          errorType === 'networkerror' ||
          errorType === 'abort';

        const isRetryableError = isHttp2Error || isNetworkError;

        // Always retry network/HTTP2 errors indefinitely
        // For other errors, retry up to 100 times as a safety measure
        if (isRetryableError || attempt < 100) {
          const backoffDelay = Math.min(Math.pow(2, Math.min(attempt, 5)) * 1000, 30000);
          const errorInfo = errorMessage || errorName || 'Unknown error';
          console.warn(`[CachedHoleRender] Attempt ${attempt + 1} failed for ${hole_url}/${assay_url}${isRetryableError ? ' (network error)' : ''}, retrying in ${backoffDelay/1000}s...`, errorInfo);
          
          setLoading(true); // Keep loading state true while retrying
          
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          return loadData(attempt + 1);
        }

        // Only set loading to false if we've truly exhausted retries (shouldn't happen for network errors)
        console.error(`[CachedHoleRender] Failed to load ${hole_url}/${assay_url} after ${attempt + 1} attempts:`, error);
        setLoading(false);
        markAsFailed(objectName, error, () => {});
        onLoadFailed?.(objectName);
      }
    };

    loadData();

    return () => {
      cancelled = true;
      stopLoading(objectName);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hole_url, assay_url, dataset, holetype, objectName, startLoading, stopLoading, markAsFailed]);

  useEffect(() => {
    const filteredData = filteredHoleIds.length > 0
      ? Object.keys(combinedData).reduce((acc, key) => {
          if (filteredHoleIds.includes(key)) {
            acc[key] = combinedData[key];
          }
          return acc;
        }, {})
      : combinedData;

    setHoleCount(Object.keys(filteredData).length);
    let assayNumber = 0;
    Object.keys(filteredData).forEach((hole, j) => {
      const data = filteredData[hole].assays;
      assayNumber += data.length;
    });
    setAssayCount(assayNumber);
    
    setFilterData(filteredData);
  }, [combinedData, filteredHoleIds]);

  if (loading) {
    return null; // or a loading component
  }

  return (
    holeCount > 0 && visible &&
    <group visible={visible} id={key} name={objectName} userData={{ mdvLayerKey: objectName }}>
      <Holes count={holeCount} holes={filterData}/>
      <SelectedHoleHighlight holes={filterData} />
      {assayCount > 0 && <Assays count={assayCount} holes={filterData}/>}
    </group>
  );
}

function Holes({ count, holes }) {
  const ref = useRef();
  const [instancesData, setInstancesData] = useState([]);
  const { hole: holeRadius } = useHoleDisplayRadii();

  useEffect(() => {
    let cancelled = false;

    const uploadHoles = async () => {
      if (!ref.current) return;
      const holeIds = Object.keys(holes);
      const idata = new Array(holeIds.length);
      let i = 0;

      for (let holeIdx = 0; holeIdx < holeIds.length; holeIdx += 1) {
        if (cancelled || !ref.current) return;
        const holeId = holeIds[holeIdx];
        const holeData = holes[holeId];
        const matrix = createHoleMatrixFromPoints(holeData.points[0], holeData.points[1]);
        ref.current.setMatrixAt(i, matrix);
        idata[i] = { ...holeData.data, holeID: holeId };
        i += 1;

        if (i % HOLE_UPLOAD_YIELD_BATCH === 0) {
          ref.current.instanceMatrix.needsUpdate = true;
          await yieldToMainThread();
        }
      }

      if (cancelled || !ref.current) return;
      ref.current.count = i;
      ref.current.instanceMatrix.needsUpdate = true;
      setInstancesData(idata);
    };

    uploadHoles();

    return () => {
      cancelled = true;
    };
  }, [holes]);

  return (
    <instancedMesh ref={ref} args={[null, null, count]} userData={{instancesData:instancesData}} name='Holes'>
      <cylinderGeometry key={holeRadius} args={[holeRadius, holeRadius, 1, 8]} />
      <meshStandardMaterial color={'#000000'} />
    </instancedMesh>
  );
}

function Assays({ count, holes }) {
  const ref = useRef();
  const [colors, setColors] = useState(new Float32Array());
  const [instancesData, setInstancesData] = useState([]);
  const { interval: intervalRadius } = useHoleDisplayRadii();

  useEffect(() => {
    let cancelled = false;

    const uploadAssays = async () => {
      if (!ref.current) return;
      const colorsArray = new Float32Array(count * 3);
      const idata = new Array(count);
      let i = 0;

      const holeIds = Object.keys(holes);
      for (let holeIdx = 0; holeIdx < holeIds.length; holeIdx += 1) {
        const data = holes[holeIds[holeIdx]].assays;
        for (let assayIdx = 0; assayIdx < data.length; assayIdx += 1) {
          if (cancelled || !ref.current) return;
          const assay = data[assayIdx];
          const matrix = assay.matrix;
          const color = assay.color;
          const assayData = assay.data;
          idata[i] = {
            holeID: assayData[assayColumns.HoleID],
            from: assayData[assayColumns.from] + ' m',
            to: assayData[assayColumns.to] + ' m',
            grade: assayData[assayColumns.grade] + ' g/t'
          };

          ref.current.setMatrixAt(i, matrix);
          const colorOffset = i * 3;
          colorsArray[colorOffset] = color.r;
          colorsArray[colorOffset + 1] = color.g;
          colorsArray[colorOffset + 2] = color.b;
          i += 1;

          if (i % ASSAY_UPLOAD_YIELD_BATCH === 0) {
            ref.current.instanceMatrix.needsUpdate = true;
            await yieldToMainThread();
          }
        }
      }

      if (cancelled || !ref.current) return;
      ref.current.count = i;
      ref.current.instanceMatrix.needsUpdate = true;
      setInstancesData(i === idata.length ? idata : idata.slice(0, i));
      setColors(i === count ? colorsArray : colorsArray.slice(0, i * 3));
    };

    uploadAssays();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holes]);

  return (
    <instancedMesh ref={ref} args={[null, null, count]} userData={{instancesData:instancesData}} name='Assays'>
      <cylinderGeometry key={intervalRadius} args={[intervalRadius, intervalRadius, 1, 8]} >
        <instancedBufferAttribute attach="attributes-color" args={[colors, 3]} />
      </cylinderGeometry>
      <meshLambertMaterial vertexColors toneMapped={false} />
    </instancedMesh>
  );
}
